import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import { commercialFeeAuthoringApi } from "@bw/shared/api";
import type {
  CommercialFeeDraftPage,
  CommercialFeeDraftReplace,
  CommercialFeePolicyDraft,
  CommercialFeePolicyVersion,
  CommercialFeePolicyWrite,
  CommercialFeeVersionPage,
  CommercialFeePublicationReview,
  CommercialPublicationRequest,
} from "@bw/shared/types/generated";

export function useFeePolicyDrafts(
  page: { limit?: number; offset?: number } = {},
): UseQueryResult<CommercialFeeDraftPage, Error> {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["fee-policy-drafts", user?.uid, page.limit, page.offset],
    queryFn: () => commercialFeeAuthoringApi.listDrafts(page),
    enabled: Boolean(user?.uid),
    retry: false,
  });
}
export function useFeePolicyDraft(
  draftId: string | undefined,
): UseQueryResult<CommercialFeePolicyDraft, Error> {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["fee-policy-draft", user?.uid, draftId],
    queryFn: () => commercialFeeAuthoringApi.getDraft(draftId!),
    enabled: Boolean(user?.uid && draftId),
    retry: false,
  });
}
export function useFeePolicyPublicationReview(
  draftId: string,
  environment: CommercialPublicationRequest["environment"],
): UseQueryResult<CommercialFeePublicationReview, Error> {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["fee-policy-publication-review", user?.uid, draftId, environment],
    queryFn: () => commercialFeeAuthoringApi.getPublicationReview(draftId, environment),
    enabled: Boolean(user?.uid && draftId),
    retry: false,
  });
}

export function usePublishFeePolicy(
  draftId: string,
): UseMutationResult<CommercialFeePolicyVersion, Error, CommercialPublicationRequest> {
  return useMutation({
    mutationFn: (data: CommercialPublicationRequest) =>
      commercialFeeAuthoringApi.publish(draftId, data),
    retry: false,
  });
}
export function useFeePolicyVersions(
  audienceOrgId: string, page: { limit?: number; offset?: number } = {},
): UseQueryResult<CommercialFeeVersionPage, Error> {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["fee-policy-versions", user?.uid, audienceOrgId, page.limit, page.offset],
    queryFn: () => commercialFeeAuthoringApi.listVersions({ audience_org_id: audienceOrgId, ...page }),
    enabled: Boolean(user?.uid && audienceOrgId),
    retry: false,
  });
}

export function useFeePolicyVersion(
  versionId: string | undefined,
): UseQueryResult<CommercialFeePolicyVersion, Error> {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["fee-policy-version", user?.uid, versionId],
    queryFn: () => commercialFeeAuthoringApi.getVersion(versionId!),
    enabled: Boolean(user?.uid && versionId),
    retry: false,
  });
}
export function useCreateFeePolicyDraft(): UseMutationResult<
  CommercialFeePolicyDraft,
  Error,
  CommercialFeePolicyWrite
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: CommercialFeePolicyWrite) =>
      commercialFeeAuthoringApi.createDraft(data),
    retry: false,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["fee-policy-drafts"] });
    },
  });
}
export function useReplaceFeePolicyDraft(
  draftId: string | undefined,
): UseMutationResult<
  CommercialFeePolicyDraft,
  Error,
  CommercialFeeDraftReplace
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: CommercialFeeDraftReplace) =>
      commercialFeeAuthoringApi.replaceDraft(draftId!, data),
    retry: false,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["fee-policy-draft"] }),
        client.invalidateQueries({ queryKey: ["fee-policy-drafts"] }),
      ]);
    },
  });
}
